import React, { useState } from 'react';
import { FaPhone, FaWhatsapp, FaEnvelope, FaMapMarkerAlt, FaClock } from 'react-icons/fa';

export default function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Simulate form submission (replace with actual API call)
      await new Promise(resolve => setTimeout(resolve, 1500));
      setSubmitMessage('Thank you for your message! We will get back to you soon.');
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      setSubmitMessage('There was an error submitting your message. Please try again.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  const contactEmail = "support@aweitflix.com";
  const contactNumber = "+256 761832669";
  const whatsappUrl = `https://wa.me/${contactNumber.replace(/\D/g, '')}`;

  return (
    <div className="max-w-6xl mx-auto px-4 py-12 bg-black text-gray-100">
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-[#FF1493] to-[#FF8C00]">
          Contact AweitFlix
        </h1>
        <p className="text-lg text-gray-400 max-w-2xl mx-auto">
          Have questions or need assistance? Reach out to us through any of these channels or fill out the form below.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Contact Information */}
        <div className="space-y-6">
          <div className="bg-gray-900 p-6 rounded-xl border border-gray-800">
            <h2 className="text-2xl font-semibold mb-6 text-white">Contact Information</h2>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="bg-[#FF1493] p-3 rounded-full">
                  <FaPhone className="text-xl text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-white">Call Us</h3>
                  <p className="text-gray-400">{contactNumber}</p>
                  <a 
                    href={`tel:${contactNumber}`}
                    className="mt-2 inline-block bg-[#FF1493] text-white px-4 py-2 rounded-md hover:bg-[#e31282] transition-colors"
                  >
                    Call Now
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-[#25D366] p-3 rounded-full">
                  <FaWhatsapp className="text-xl text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-white">WhatsApp</h3>
                  <p className="text-gray-400">Chat with us on WhatsApp</p>
                  <a 
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-block bg-[#25D366] text-white px-4 py-2 rounded-md hover:bg-[#1da851] transition-colors"
                  >
                    Message on WhatsApp
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-[#FF8C00] p-3 rounded-full">
                  <FaEnvelope className="text-xl text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-white">Email Us</h3>
                  <p className="text-gray-400">{contactEmail}</p>
                  <a 
                    href={`mailto:${contactEmail}`}
                    className="mt-2 inline-block bg-[#FF8C00] text-white px-4 py-2 rounded-md hover:bg-[#e67e22] transition-colors"
                  >
                    Send Email
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-gradient-to-r from-[#FF1493] to-[#FF8C00] p-3 rounded-full">
                  <FaMapMarkerAlt className="text-xl text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-white">Our Location</h3>
                  <p className="text-gray-400">Kampala, Uganda</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-900 p-6 rounded-xl border border-gray-800">
            <div className="flex items-center mb-4">
              <FaClock className="text-2xl mr-3 text-[#FF8C00]" />
              <h2 className="text-2xl font-semibold text-white">Business Hours</h2>
            </div>
            <ul className="space-y-3">
              <li className="flex justify-between text-gray-300">
                <span className="font-medium">Monday - Friday</span>
                <span>8:00 AM - 5:00 PM</span>
              </li>
              <li className="flex justify-between text-gray-300">
                <span className="font-medium">Saturday</span>
                <span>9:00 AM - 2:00 PM</span>
              </li>
              <li className="flex justify-between text-gray-300">
                <span className="font-medium">Sunday</span>
                <span>Closed</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-gray-900 p-6 rounded-xl border border-gray-800">
          <h2 className="text-2xl font-semibold mb-6 text-white">Send Us a Message</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">Your Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md focus:ring-2 focus:ring-[#FF1493] focus:border-[#FF1493] outline-none transition text-white"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md focus:ring-2 focus:ring-[#FF1493] focus:border-[#FF1493] outline-none transition text-white"
              />
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-300 mb-1">Subject</label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md focus:ring-2 focus:ring-[#FF1493] focus:border-[#FF1493] outline-none transition text-white"
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1">Your Message</label>
              <textarea
                id="message"
                name="message"
                rows="5"
                value={formData.message}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md focus:ring-2 focus:ring-[#FF1493] focus:border-[#FF1493] outline-none transition text-white"
              ></textarea>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full bg-gradient-to-r from-[#FF1493] to-[#FF8C00] text-white py-3 px-4 rounded-md hover:opacity-90 transition-colors ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>

            {submitMessage && (
              <div className={`mt-4 p-3 rounded-md ${submitMessage.includes('error') ? 'bg-red-900 text-red-100' : 'bg-green-900 text-green-100'}`}>
                {submitMessage}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}